// What the Business/Location switcher may show (Phase 1 / T1.7).
//
// Pure and leaf-testable: it takes the discovery payload the server already
// narrowed to this member's memberships and scope, and decides what is
// SWITCHABLE. The two are not the same question, and keeping them apart is the
// point of this file:
//
//   /api/businesses answers "what may this member reach at all" — and must keep
//   returning PROVISIONAL businesses, because `/app` resolution needs them to
//   send an owner back into `/app/start?resume={id}` (T1.6). Making that
//   endpoint active-only would silently strand every half-finished onboarding.
//
//   The switcher answers "what may this member ACT AS". A provisional business
//   has no operational surface — every ordinary route refuses it at the
//   resolver (T1.6B3) — so offering it as a destination would be offering a
//   door onto a wall.
//
// The filtering therefore lives HERE, in the UI's own model, never in the
// shared discovery contract.
import type { DiscoveredBusinessLike, DiscoveryResponse } from '@/lib/active-tenant-resolve';

export interface SwitchableLocation {
  id: string;
  name: string;
  primary: boolean;
}

export interface SwitchableBusiness {
  id: string;
  name: string;
  locations: SwitchableLocation[];
}

/**
 * ACTIVE businesses only, each carrying only the Locations the server already
 * said this member may reach.
 *
 * Nothing here widens what the server sent: an out-of-scope sibling never
 * arrives in the payload, so it cannot be rendered, counted, linked or leaked
 * as a disabled row. Archived businesses never arrive either (discovery drops
 * them). A business whose reachable-location list is empty is still listed —
 * that is an honest "you have this business but no location in it here", and
 * hiding it would tell a scoped member their business does not exist.
 */
export function switchableBusinesses(discovery: DiscoveryResponse | null): SwitchableBusiness[] {
  if (!discovery) return [];
  return discovery.businesses
    .filter((b: DiscoveredBusinessLike) => b.lifecycleStatus === 'active')
    .map((b) => ({
      id: b.id,
      name: b.name,
      locations: b.locations.map((l) => ({ id: l.id, name: l.name, primary: l.primary })),
    }));
}

/**
 * Is this exact pair one the server offered? The switcher writes `pb.active`
 * on selection, and navigation memory must only ever record a pair the caller
 * was told they may reach — a stale or hand-edited value must not survive into
 * it. Authorization is still the server's on every request; this only stops
 * the client from REMEMBERING something it was never offered.
 */
export function isOfferedPair(
  businesses: SwitchableBusiness[],
  businessId: string,
  locationId: string,
): boolean {
  if (!businessId || !locationId) return false;
  const business = businesses.find((b) => b.id === businessId);
  return Boolean(business?.locations.some((l) => l.id === locationId));
}

/** The Location a Business should open on: its primary, else the first offered. */
export function defaultLocationFor(business: SwitchableBusiness): SwitchableLocation | null {
  return business.locations.find((l) => l.primary) ?? business.locations[0] ?? null;
}
